import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { interval } from 'rxjs';
import { APP_VERSION } from '../app-version';

/**
 * v1.6.538 → v1.6.764 — Automatic version checker service
 *
 * Polls the backend every 5 minutes to check if a new dashboard version
 * has been deployed. When a new version is detected, shows a persistent
 * toast notification with explicit Reload / Dismiss controls. **No
 * automatic reload** — the operator decides when to interrupt their
 * work.
 *
 * v1.6.764 — Operator-reported: "new version available toast is not
 * dismissable and it then it freezes the site so i have to close it
 * out and go back in". Two failure modes the v1.6.538 design hit in
 * production:
 *
 *   1. **Mid-action hard-reload destroys work.** The original
 *      implementation fired `window.location.reload()` 3 seconds after
 *      detecting a new version, with no way to cancel. Operator in the
 *      middle of editing a form lost all in-progress state when the
 *      timer fired.
 *   2. **Reload-loop on cached stale bundle.** If the browser's HTTP
 *      cache served the old JS bundle after the reload, `APP_VERSION`
 *      was still the old one on the next 5-minute check → the toast
 *      reappeared → 3 seconds later another reload → infinite cycle.
 *      The operator's escape: close the tab entirely.
 *
 * Fixes:
 *   - Toast is interactive (Reload now + Dismiss + X), never auto-reloads.
 *   - Dismiss persists in sessionStorage keyed by version so the same
 *     toast doesn't reappear on the next 5-minute check during this
 *     session.
 *   - Reload-attempted flag in sessionStorage detects the stale-cache
 *     loop. If we already tried reloading for THIS server version and
 *     the next check STILL shows us behind, we show a different toast
 *     guiding the operator to do a hard-reload manually (Ctrl+Shift+R)
 *     rather than firing yet another normal reload.
 *   - Single-toast invariant via in-memory guard — repeated checks
 *     while the toast is already on screen never stack duplicates.
 */
@Injectable({
  providedIn: 'root',
})
export class VersionCheckerService {
  private currentVersion = APP_VERSION;
  private checkIntervalMs = 5 * 60 * 1000; // 5 minutes

  /** Versions the operator has dismissed in this session — never
   *  re-show the toast for these until session ends or a NEWER version
   *  appears. Source of truth lives in sessionStorage; this in-memory
   *  cache avoids re-reading every interval tick. */
  private dismissedInSession = new Set<string>();

  /** In-memory guard so repeated 5-minute checks while the toast is
   *  already on screen don't stack duplicate toasts. */
  private toastShownForVersion: string | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Start checking for version updates.
   * Call this once in AppComponent ngOnInit.
   */
  startChecking() {
    // Check immediately on startup.
    this.checkVersion();
    // Then check every 5 minutes.
    interval(this.checkIntervalMs).subscribe(() => {
      this.checkVersion();
    });
  }

  private checkVersion() {
    const timestamp = new Date().getTime(); // Cache buster
    /**
     * v1.6.700 — Read as text and parse defensively.
     *
     * With the default JSON responseType, a 200 response whose body
     * isn't JSON (classically the SPA index.html served as a fallback
     * when /assets/version.json is missing) is turned by HttpClient
     * into `HttpErrorResponse {status: 200, ok: false}` — a confusing,
     * noisy error with no actionable signal, and one the old `error`
     * handler couldn't distinguish from a real network failure.
     *
     * Reading the body as text lets us JSON.parse inside a try/catch,
     * detect the HTML-fallback case explicitly, log something useful,
     * and never misread an HTML page as a version string.
     */
    this.http.get(`/assets/version.json?t=${timestamp}`, { responseType: 'text' })
      .subscribe({
        next: (body) => {
          let serverVersion: string | undefined;
          try {
            const parsed = JSON.parse(body) as { version?: string };
            serverVersion = parsed?.version;
          } catch {
            const looksLikeHtml = /^\s*<(?:!doctype|html)/i.test(body ?? '');
            console.warn(
              '[version-checker] version.json did not return JSON' +
              (looksLikeHtml
                ? ' (got an HTML page — the asset is likely missing at /assets/version.json).'
                : '.') +
              ' Skipping this check.',
            );
            return;
          }
          if (!serverVersion || serverVersion === this.currentVersion) return;

          // v1.6.764 — Guard: already dismissed this version this session.
          if (this.isDismissedInSession(serverVersion)) {
            return;
          }
          // v1.6.764 — Guard: toast already on screen for this version.
          if (this.toastShownForVersion === serverVersion) {
            return;
          }
          console.log(`[version-checker] New version detected: ${serverVersion} (current: ${this.currentVersion})`);

          // v1.6.764 — Reload-loop detector. If we already attempted a
          // reload for THIS server version and we're STILL on the old
          // version, the HTTP cache (or a service worker) is serving
          // stale JS. Show a "stuck" toast that explains the situation
          // and tells the operator to do a hard-reload manually, instead
          // of firing yet another normal reload that won't help.
          if (this.wasReloadAttempted(serverVersion)) {
            this.showStuckToast(serverVersion);
            return;
          }
          this.showUpdateToast(serverVersion);
        },
        error: (err) => {
          // Genuine transport error (offline, 5xx, CORS). Silently warn —
          // don't disrupt user experience if the version check fails.
          console.warn('[version-checker] Failed to check version:', err);
        },
      });
  }

  /** SessionStorage key for "operator dismissed this new version". */
  private dismissedKey(version: string): string {
    return `version-checker:dismissed:${version}`;
  }

  /** SessionStorage key for "we already tried reloading for this new
   *  version" (loop detector for stale-cache scenarios). */
  private reloadAttemptedKey(version: string): string {
    return `version-checker:reload-attempted:${version}`;
  }

  private isDismissedInSession(version: string): boolean {
    if (this.dismissedInSession.has(version)) return true;
    try {
      if (sessionStorage.getItem(this.dismissedKey(version))) {
        this.dismissedInSession.add(version);
        return true;
      }
    } catch { /* sessionStorage may be unavailable (private mode, etc.) */ }
    return false;
  }

  private markDismissed(version: string): void {
    this.dismissedInSession.add(version);
    try { sessionStorage.setItem(this.dismissedKey(version), '1'); } catch { /* ignore */ }
  }

  private wasReloadAttempted(version: string): boolean {
    try { return !!sessionStorage.getItem(this.reloadAttemptedKey(version)); }
    catch { return false; }
  }

  private markReloadAttempted(version: string): void {
    try { sessionStorage.setItem(this.reloadAttemptedKey(version), '1'); } catch { /* ignore */ }
  }

  /** v1.6.764 — Interactive toast with explicit Reload / Dismiss
   *  controls. No auto-reload. Pure DOM (no Angular dependencies)
   *  because the service is provided in root and runs outside any
   *  component context. */
  private showUpdateToast(newVersion: string): void {
    this.toastShownForVersion = newVersion;
    this.injectAnimationStyle();

    const toast = document.createElement('div');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.dataset['versionCheckerToast'] = '1';
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #ffffff;
      color: #1f2937;
      padding: 14px 16px;
      border-radius: 10px;
      border: 1px solid #d1d5db;
      box-shadow: 0 10px 25px rgba(0,0,0,0.12), 0 4px 6px rgba(0,0,0,0.06);
      z-index: 9999;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 14px;
      max-width: 360px;
      animation: vcSlideIn 0.3s ease-out;
      display: flex;
      flex-direction: column;
      gap: 10px;
    `;

    // Header row: title + close X.
    const header = document.createElement('div');
    header.style.cssText = 'display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;';
    const title = document.createElement('div');
    title.style.cssText = 'font-weight: 600; line-height: 1.35;';
    title.textContent = 'New version available';
    const closeX = document.createElement('button');
    closeX.type = 'button';
    closeX.setAttribute('aria-label', 'Dismiss');
    closeX.textContent = '×';
    closeX.style.cssText = 'background: transparent; border: 0; color: #9ca3af; font-size: 20px; line-height: 1; cursor: pointer; padding: 0 2px;';
    closeX.addEventListener('click', () => this.dismissToast(toast, newVersion));
    header.appendChild(title);
    header.appendChild(closeX);

    // Body text.
    const body = document.createElement('div');
    body.style.cssText = 'color: #4b5563; font-size: 13px; line-height: 1.45;';
    body.textContent = `Version ${newVersion} is available. Reload to get the latest changes — your unsaved work will be lost.`;

    // Action row: Reload now (primary) + Dismiss (secondary).
    const actions = document.createElement('div');
    actions.style.cssText = 'display: flex; gap: 8px; justify-content: flex-end; margin-top: 2px;';

    const dismissBtn = document.createElement('button');
    dismissBtn.type = 'button';
    dismissBtn.textContent = 'Dismiss';
    dismissBtn.style.cssText = 'background: transparent; border: 1px solid #d1d5db; color: #4b5563; padding: 6px 12px; border-radius: 6px; font-size: 13px; cursor: pointer; font-weight: 500;';
    dismissBtn.addEventListener('click', () => this.dismissToast(toast, newVersion));

    const reloadBtn = document.createElement('button');
    reloadBtn.type = 'button';
    reloadBtn.textContent = 'Reload now';
    reloadBtn.style.cssText = 'background: #64d2d3; border: 1px solid #64d2d3; color: #ffffff; padding: 6px 12px; border-radius: 6px; font-size: 13px; cursor: pointer; font-weight: 600;';
    reloadBtn.addEventListener('click', () => {
      this.markReloadAttempted(newVersion);
      // Clean up DOM before reload — defensive, doesn't matter since
      // the page is about to vanish anyway.
      try { toast.remove(); } catch { /* ignore */ }
      console.log('[version-checker] Operator clicked Reload — reloading page…');
      window.location.reload();
    });

    actions.appendChild(dismissBtn);
    actions.appendChild(reloadBtn);

    toast.appendChild(header);
    toast.appendChild(body);
    toast.appendChild(actions);
    document.body.appendChild(toast);
  }

  /** v1.6.764 — Shown when we already tried a reload for this version
   *  and the operator is STILL on the old bundle. Tells them to do a
   *  hard-reload (Ctrl+Shift+R / Cmd+Shift+R) so they bypass cache —
   *  a normal reload won't help, and firing it would just put them
   *  back in the same loop. */
  private showStuckToast(newVersion: string): void {
    this.toastShownForVersion = newVersion;
    this.injectAnimationStyle();

    const toast = document.createElement('div');
    toast.setAttribute('role', 'alert');
    toast.dataset['versionCheckerToast'] = '1';
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #fef3c7;
      color: #78350f;
      padding: 14px 16px;
      border-radius: 10px;
      border: 1px solid #fcd34d;
      box-shadow: 0 10px 25px rgba(0,0,0,0.12), 0 4px 6px rgba(0,0,0,0.06);
      z-index: 9999;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 13px;
      max-width: 380px;
      animation: vcSlideIn 0.3s ease-out;
      display: flex;
      flex-direction: column;
      gap: 8px;
    `;

    const header = document.createElement('div');
    header.style.cssText = 'display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;';
    const title = document.createElement('div');
    title.style.cssText = 'font-weight: 600;';
    title.textContent = `Update ${newVersion} needs a hard refresh`;
    const closeX = document.createElement('button');
    closeX.type = 'button';
    closeX.setAttribute('aria-label', 'Dismiss');
    closeX.textContent = '×';
    closeX.style.cssText = 'background: transparent; border: 0; color: #92400e; font-size: 20px; line-height: 1; cursor: pointer; padding: 0 2px;';
    closeX.addEventListener('click', () => this.dismissToast(toast, newVersion));
    header.appendChild(title);
    header.appendChild(closeX);

    const body = document.createElement('div');
    body.style.cssText = 'line-height: 1.45;';
    const macOs = /Mac|iPad|iPhone|iPod/i.test(navigator?.userAgent ?? '');
    body.innerHTML = `Your browser is caching the old version. Press <b>${macOs ? 'Cmd+Shift+R' : 'Ctrl+Shift+R'}</b> to force-refresh, or close this tab and reopen it.`;

    toast.appendChild(header);
    toast.appendChild(body);
    document.body.appendChild(toast);
  }

  private dismissToast(toast: HTMLElement, version: string): void {
    this.markDismissed(version);
    this.toastShownForVersion = null;
    try { toast.remove(); } catch { /* ignore */ }
    console.log(`[version-checker] Operator dismissed toast for ${version}. Will not nag again this session.`);
  }

  /** Inject the slideIn keyframes exactly once, even if the service
   *  ends up running multiple checks before the first toast is
   *  dismissed (it shouldn't, with the single-toast guard, but
   *  defensive). */
  private injectAnimationStyle(): void {
    if (document.getElementById('vc-toast-anim')) return;
    const style = document.createElement('style');
    style.id = 'vc-toast-anim';
    style.textContent = `@keyframes vcSlideIn { from { transform: translateX(400px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`;
    document.head.appendChild(style);
  }
}
